<template>
  <Modal class="max-w-[500px]!">
    <Modal.Body>
      <ShieldIcon class="mx-auto mb-2.5 size-6 stroke-gray-800" />

      <h2
        class="mb-[18px] text-center text-[19px] font-medium leading-[26px] text-gray-800"
      >
        Confirmation of right to add work to Dacr
      </h2>

      <p class="text-[11px] leading-[17px] text-gray-800">
        By continuing, I represent and warrant that I own or control the
        copyright (or exclusive rights) in the work(s) submitted, or I am duly
        authorized to submit them for the rights holder, and that Dacr may rely
        on this. Submitting work without rights or providing false information
        may be unlawful (including fraud) and may expose me to civil and
        criminal liability under applicable law.
      </p>

      <div class="my-[14px] h-px w-full bg-[#DFDFDF]" />

      <div
        class="rounded-xl border border-[#EEEDEC] bg-[#F6F6F6] px-[14px] pt-2 pb-2.5"
      >
        <span class="text-[13px] font-medium leading-[17px] text-gray-800">
          Selected jurisdiction to be registered
        </span>

        <div class="mt-2.5 flex items-center justify-between">
          <div class="flex items-center">
            <div
              class="z-10 flex size-8 items-center justify-center rounded-full border border-gray-600/15 bg-white"
              :class="{
                '-mr-3': additionalOrigins.length || canEditJurisdictions,
              }"
            >
              <DacrMarkIcon class="size-6 text-gray-800" />
            </div>
            <Avatar
              v-for="origin in additionalOrigins"
              :key="origin.id"
              :src="origin.flag.url"
              :size="32"
              class="z-10 -mr-3 border-[3px] border-[#F6F6F6]"
            />
            <button
              v-if="canEditJurisdictions"
              type="button"
              class="group flex size-8 cursor-pointer items-center justify-center rounded-full border border-dashed border-gray-300/50 transition-all hover:border-gray-300/75"
              @click="handleChange"
            >
              <AddIcon
                class="size-3 text-gray-400 transition-all group-hover:text-gray-500"
              />
            </button>
          </div>

          <button
            v-if="canEditJurisdictions"
            type="button"
            class="text-link text-[13px]"
            @click="handleChange"
          >
            Change
          </button>
        </div>
      </div>

      <div
        v-if="showPrices"
        class="mt-2 flex flex-col gap-y-1.5 rounded-xl border border-[#EEEDEC] px-[14px] py-[7px]"
      >
        <div class="flex items-center justify-between">
          <span class="text-[13px] leading-[18px] text-gray-600">
            Works being registered
          </span>
          <span class="text-[14px] leading-[18px] text-gray-800">
            {{ uploadCount }}
          </span>
        </div>

        <div class="flex items-center justify-between">
          <div class="flex items-center gap-x-2">
            <span class="text-[13px] font-medium leading-[18px] text-gray-800">
              Total to be billed
            </span>
            <button
              type="button"
              class="flex items-center gap-0.5 text-link text-[13px]"
              @click="handleDetails"
            >
              Details
              <ChevronRightIcon class="size-3" />
            </button>
          </div>
          <span class="text-[17px] font-medium leading-[18px] text-gray-800">
            {{ totalCost }}
          </span>
        </div>
      </div>

      <div class="mt-[14px] flex items-start gap-x-2">
        <Checkbox
          v-model="agreeToTerms"
          :class="['mt-px shrink-0', { 'cb-attention': !agreeToTerms }]"
        />
        <p
          class="cursor-pointer text-[12px] leading-[17px] text-gray-600"
          @click="agreeToTerms = !agreeToTerms"
        >
          <template v-if="showPrices">
            I represent and warrant I have the rights/authorization to submit
            these work(s), authorize Dacr to charge the amounts shown above to
            my card on file, and agree to the
            <a class="text-link" href="/legal/terms" target="_blank" @click.stop
              >Dacr Terms</a
            >,
            <a
              class="text-link"
              href="/legal/privacy"
              target="_blank"
              @click.stop
              >Privacy Policy</a
            >, and
            <a
              class="text-link"
              href="/legal/payments"
              target="_blank"
              @click.stop
              >Payments Policy</a
            >. All fees are final except where required by law.
          </template>
          <template v-else>
            I represent and warrant I have the rights/authorization to submit
            these work(s), and agree to the
            <a class="text-link" href="/legal/terms" target="_blank" @click.stop
              >Dacr Terms</a
            >,
            <a
              class="text-link"
              href="/legal/privacy"
              target="_blank"
              @click.stop
              >Privacy Policy</a
            >, and
            <a
              class="text-link"
              href="/legal/payments"
              target="_blank"
              @click.stop
              >Payments Policy</a
            >.
          </template>
        </p>
      </div>

      <Button
        variant="primary"
        size="lg"
        class="mt-2.5 w-full"
        :disabled="disableButton"
        @click="handleAgree"
      >
        Agree, continue
      </Button>
      <button
        type="button"
        class="mx-auto mt-2 block text-link text-[14px]"
        @click="close()"
      >
        Cancel upload
      </button>
    </Modal.Body>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Modal, Button, Checkbox, Avatar, useModal } from '@dacr/library'
import { DacrMarkIcon, AddIcon, ChevronRightIcon } from '@dacr/icons'
import ShieldIcon from '@/assets/svg/icons/ShieldIcon.svg'
import { graphql } from '@/shared/graphql/router'
import { useQuery } from '@vue/apollo-composable'
import { openModal, Modals } from '@/shared/dialogs/modals'
import { useUserStore } from '@/shared/stores/user'
import { useOriginStore } from '@/shared/stores/origin'
import { useBillingStore } from '@/features/billing/store'
import { useFeatureFlag } from '@/shared/composables/useFeatureFlag'
import { FeatureFlag } from '@/shared/stores/featureFlags'
import { PlanType } from '@/shared/graphql/legacy/generated'
import { formatPrice } from '@/shared/utils/money'
import type {
  EstimateRegistrationCostQuery,
  EstimateRegistrationCostQueryVariables,
} from '@/shared/graphql/router/graphql'

const props = defineProps<{ uploadCount: number }>()

const { close, triggerEvent } = useModal()
const userStore = useUserStore()
const originStore = useOriginStore()
const billingStore = useBillingStore()
const originFlagEnabled = useFeatureFlag(FeatureFlag.Origin)

const userOrigin = userStore.user?.origin

type EstimatedCost = EstimateRegistrationCostQuery['estimateRegistrationCost']

const selectedOriginIds = ref<string[]>(
  userOrigin && userOrigin !== 'Dacr' ? ['Dacr', userOrigin] : ['Dacr']
)

const selectedOrigins = computed(() =>
  originStore.origins.filter((origin) =>
    selectedOriginIds.value.includes(origin.id)
  )
)

const additionalOrigins = computed(() =>
  selectedOrigins.value.filter((origin) => origin.id !== 'Dacr')
)

const showPrices = computed(
  () => billingStore.billing?.plan.type === PlanType.Pro
)

const canEditJurisdictions = computed(() => originFlagEnabled.value)

const ESTIMATE_REGISTRATION_COST = graphql(`
  query EstimateRegistrationCost($input: EstimateRegistrationCostInput!) {
    estimateRegistrationCost(input: $input) {
      total {
        amount
        formatted
      }
      breakdown {
        description
        origin {
          id
          name
          flag {
            url
          }
        }
        total {
          amount
          formatted
        }
        breakdown {
          id
          name
          total {
            amount
            formatted
          }
        }
      }
    }
  }
`)

const { result: estimateResult } = useQuery<
  EstimateRegistrationCostQuery,
  EstimateRegistrationCostQueryVariables
>(
  ESTIMATE_REGISTRATION_COST,
  () => ({
    input: {
      origins: [...selectedOriginIds.value],
      quantity: props.uploadCount,
    },
  }),
  { clientId: 'router', enabled: showPrices }
)

const estimatedCost = ref<EstimatedCost>(null)

watch(
  () => estimateResult.value?.estimateRegistrationCost,
  (cost) => {
    if (cost) estimatedCost.value = cost
  },
  { immediate: true }
)

const totalCost = computed(() =>
  formatPrice({ amount: estimatedCost.value?.total.amount || 0 })
)

const agreeToTerms = ref(false)

const disableButton = computed(
  () => !agreeToTerms.value || (showPrices.value && !estimatedCost.value)
)

const handleAgree = () => triggerEvent('success', [...selectedOriginIds.value])

const handleDetails = () =>
  openModal(Modals.ConfirmUploadDetails, {
    stack: true,
    props: { estimatedCost: estimatedCost.value },
  })

const handleChange = () =>
  openModal(Modals.UploadJurisdictions, {
    stack: true,
    props: {
      selectedOriginIds: [...selectedOriginIds.value],
      quantity: props.uploadCount,
      lockedOriginId: userOrigin ?? null,
    },
    events: {
      success(data) {
        const { ids, estimatedCost: cost } = data as {
          ids: string[]
          estimatedCost: EstimatedCost
        }
        selectedOriginIds.value = ids
        if (cost) estimatedCost.value = cost
      },
    },
  })
</script>

<style scoped>
.cb-attention :deep(span) {
  border-color: var(--color-green-500);
  box-shadow: 0 0 0 2px
    color-mix(in srgb, var(--color-green-200) 50%, transparent);
}
</style>
